import React, { useEffect, useRef, useState } from "react";
import { Bell, LogOut, ChevronDown, Menu, ShieldCheck, BrainCircuit } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import HeaderClock from "@/components/HeaderClock";
import { playWaterDrop } from "@/lib/notificationSound";

export default function Header({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [notifs, setNotifs] = useState([]);
  const [aiExhausted, setAiExhausted] = useState(false);
  const [aiConfigured, setAiConfigured] = useState(true); // assume OK until /ai-status confirms
  const lastUnreadRef = useRef(null);
  const navigate = useNavigate();

  const loadNotifs = async () => {
    try {
      const res = await api.get("/notifications");
      const list = res.data;
      const unreadCount = list.filter(n => !n.read).length;
      // Play water-drop sound when unread count *increases*, but never on initial mount.
      if (lastUnreadRef.current !== null && unreadCount > lastUnreadRef.current) {
        playWaterDrop();
      }
      lastUnreadRef.current = unreadCount;
      setNotifs(list);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadNotifs();
    const loadAiStatus = async () => {
      try {
        const res = await api.get("/system/ai-status");
        setAiExhausted(!!res.data?.exhausted);
        setAiConfigured(res.data?.configured !== false);  // default true when not provided
      } catch { /* ignore */ }
    };
    loadAiStatus();
    const i = setInterval(() => { loadNotifs(); loadAiStatus(); }, 30000);
    return () => clearInterval(i);
  }, []);

  const unread = notifs.filter(n => !n.read).length;

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    loadNotifs();
  };

  const consultantName = user?.full_name || user?.email || "";
  const finmaNr = user?.finma_register_nr || "";

  return (
    <header className="h-14 sm:h-16 border-b border-border bg-card px-3 sm:px-6 lg:px-8 flex items-center gap-2 sm:gap-3" data-testid="app-header">
      {/* Mobile hamburger */}
      <Button
        variant="ghost" size="icon"
        className="lg:hidden -ml-1"
        onClick={onToggleSidebar}
        data-testid="sidebar-toggle-btn"
        aria-label="Menü öffnen"
      >
        <Menu className="w-5 h-5" strokeWidth={1.5} />
      </Button>

      {/* LEFT cluster: notifications + consultant name + FINMA number */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full relative shrink-0" data-testid="notifications-btn">
            <Bell className={`w-4 h-4 ${unread > 0 ? "text-prime" : ""}`} strokeWidth={1.5} />
            {unread > 0 && (
              <>
                <span className="absolute -top-0.5 -right-0.5 inline-flex h-3 w-3" aria-hidden="true">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-prime opacity-75 animate-ping" />
                </span>
                <Badge
                  className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 bg-prime text-white text-[10px] rounded-full z-10 flex items-center justify-center"
                  data-testid="notifications-unread-badge"
                >
                  {unread > 99 ? "99+" : unread}
                </Badge>
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-96 p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="label-uppercase">{t.notifications}</span>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-prime hover:underline" data-testid="mark-all-read">
                {t.mark_all_read}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-auto">
            {notifs.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">{t.no_notifications}</div>
            ) : notifs.map(n => (
              <div
                key={n.id}
                onClick={async () => {
                  try { await api.patch(`/notifications/${n.id}/read`); } catch { /* ignore */ }
                  loadNotifs();
                  // Navigate to the relevant entity
                  if (n.kind === "chat_message") {
                    navigate("/chat");
                    return;
                  }
                  let clientId = n.related_client_id;
                  if (!clientId && n.related_antrag_id) {
                    try {
                      const r = await api.get(`/antraege/${n.related_antrag_id}`);
                      clientId = r.data?.client_id;
                    } catch { /* antrag may have been deleted */ }
                  }
                  if (clientId) {
                    const isAntrag = !!n.related_antrag_id || n.kind === "antrag_pending";
                    navigate(`/clients/${clientId}${isAntrag ? "?tab=antraege" : ""}`);
                  } else if (n.related_policy_id) {
                    navigate(`/policies?policy=${n.related_policy_id}`);
                  }
                }}
                className={`p-4 border-b border-border cursor-pointer hover:bg-secondary ${!n.read ? "bg-red-50/40" : ""}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <div className="w-1.5 h-1.5 bg-prime rounded-full mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Consultant name + FINMA number (same row as bell) */}
      <div className="flex items-center gap-2 min-w-0" data-testid="consultant-info">
        <span className="font-semibold text-sm truncate" data-testid="consultant-name">
          {consultantName}
        </span>
        {finmaNr && (
          <span
            className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-prime/40 bg-prime/10 text-[10px] font-bold uppercase tracking-widest text-prime shrink-0"
            data-testid="consultant-finma"
            title="FINMA Register-Nr."
          >
            <ShieldCheck className="w-3 h-3" strokeWidth={2} />
            FINMA · {finmaNr}
          </span>
        )}
      </div>

      <div className="flex-1" />

      {/* AI status warning badge */}
      {!aiConfigured ? (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-rose-400 bg-rose-50 dark:bg-rose-900/20 text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 shrink-0"
          data-testid="ai-not-configured-warning"
          title="EMERGENT_LLM_KEY non configurata. Le funzioni AI (estrazione documenti, contatti, finanzanalyse) sono temporaneamente disabilitate. Il CRM continua a funzionare normalmente per tutte le altre operazioni (CRUD, calcoli, commissioni, dossier)."
        >
          <BrainCircuit className="w-3.5 h-3.5" strokeWidth={2} />
          <span className="hidden sm:inline">KI deaktiviert · CRM läuft normal</span>
          <span className="sm:hidden">KI off</span>
        </span>
      ) : aiExhausted ? (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 shrink-0"
          data-testid="ai-budget-warning"
          title="Das KI-Guthaben (Universal Key) ist aufgebraucht. Dokument-Extraktion und automatische Provisionsberechnung sind pausiert. Aufladen: Profil → Universal Key → Add Balance."
        >
          <BrainCircuit className="w-3.5 h-3.5" strokeWidth={2} />
          <span className="hidden sm:inline">KI-Guthaben aufgebraucht</span>
          <span className="sm:hidden">KI</span>
        </span>
      ) : null}

      <HeaderClock />

      {/* Workspace chip */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2 rounded-md h-9 pl-3" data-testid="profile-btn">
            <span className="text-sm font-medium hidden sm:inline">Wertia Finanz GmbH</span>
            <span className="text-sm font-medium sm:hidden">Wertia</span>
            <ChevronDown className="w-3 h-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="label-uppercase text-muted-foreground">{t.profile}</DropdownMenuLabel>
          <DropdownMenuItem disabled>{user?.email}</DropdownMenuItem>
          <DropdownMenuItem disabled className="text-xs text-muted-foreground">{user?.role}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/security")} data-testid="security-btn">
            <ShieldCheck className="w-4 h-4 mr-2" /> Sicherheit & 2FA
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> {t.logout}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
